<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Themeable } from '#theme'
import UIPopover from './Popover.vue'
import UISidePanel from './SidePanel.vue'
import { computed, useDefaults, useDisplay, useI18n, useRoute, useTheme, watch } from '#imports'

export interface UIUserMenuPanelProps extends Themeable {
    /** `null` for a player without one, or when the nameservice failed. */
    username?: string | null
    usernameLoading?: boolean
    displayName?: string
    bonusCount?: number
    activator?: HTMLElement | ComponentPublicInstance | null
}

const _props = withDefaults(defineProps<UIUserMenuPanelProps>(), {
    username: null,
    usernameLoading: false,
    displayName: '',
    bonusCount: undefined,
    color: undefined,
    activator: null,
})

const props = useDefaults({
    props: _props,
    defaults: {
        color: 'primary',
    },
})

const { breakpoints } = useDisplay()
const { themeClass } = useTheme(() => ({ color: props.color }))
const { t } = useI18n()

const modelValue = defineModel<boolean>({ default: false })

defineEmits<{
    deposit: [event: MouseEvent]
    bonuses: [event: MouseEvent]
    logout: [event: MouseEvent]
}>()

defineSlots<{
    default: () => any
    /** Rendered in the footer, above the logout button. */
    footerLeading: () => any
}>()

const showBonusBadge = computed<boolean>(() => typeof props.bonusCount === 'number' && props.bonusCount > 0)

const isMobile = computed<boolean>(() => breakpoints.value.isSmaller('md'))

const shell = computed(() => (isMobile.value ? UISidePanel : UIPopover))

const shellProps = computed<Record<string, unknown>>(() =>
    isMobile.value
        ? { side: 'right', background: 'var(--background, #fff)' }
        : { activator: props.activator, width: '360px' },
)

const route = useRoute()
watch(() => route.fullPath, () => {
    if (modelValue.value) modelValue.value = false
})
</script>

<template>
    <component
        :is="shell"
        v-model="modelValue"
        class="user-menu-panel"
        v-bind="shellProps"
        :aria-label="t('core.components.UIUserMenuPanel.panelAriaLabel')"
    >
        <template #header>
            <div
                class="user-menu-panel__header"
                :class="{ 'user-menu-panel__header--dropdown': !isMobile }"
            >
                <div class="user-menu-panel__identity">
                    <span
                        v-if="props.usernameLoading"
                        class="user-menu-panel__username-skeleton"
                        data-test="user-menu-panel-username-skeleton"
                        aria-hidden="true"
                    />
                    <p v-else-if="props.username" class="user-menu-panel__username">
                        {{ props.username }}
                    </p>
                    <p v-if="props.displayName" class="user-menu-panel__display-name">
                        {{ props.displayName }}
                    </p>
                </div>

                <button
                    v-if="isMobile"
                    type="button"
                    class="user-menu-panel__close"
                    :aria-label="t('core.components.UIUserMenuPanel.closeAriaLabel')"
                    @click="modelValue = false"
                >
                    <UIIcon name="close" size="16" />
                </button>
            </div>
        </template>

        <div class="user-menu-panel__body" :class="themeClass">
            <button
                type="button"
                class="user-menu-panel__cta user-menu-panel__cta--primary"
                @click="$emit('deposit', $event)"
            >
                <UIIcon name="credits" size="20" class="user-menu-panel__cta-icon" />
                <span class="user-menu-panel__cta-text">
                    <span class="user-menu-panel__cta-title">{{ t('core.components.UIUserMenuPanel.depositTitle') }}</span>
                    <span class="user-menu-panel__cta-subtitle">{{ t('core.components.UIUserMenuPanel.depositSubtitle') }}</span>
                </span>
                <UIIcon name="arrow-right" size="10" class="user-menu-panel__cta-chevron" />
            </button>

            <button
                type="button"
                class="user-menu-panel__cta user-menu-panel__cta--secondary"
                @click="$emit('bonuses', $event)"
            >
                <UIIcon name="bonus" size="20" class="user-menu-panel__cta-icon" />
                <span class="user-menu-panel__cta-title">{{ t('core.components.UIUserMenuPanel.bonusesTitle') }}</span>
                <span v-if="showBonusBadge" class="user-menu-panel__badge">{{ props.bonusCount }}</span>
                <UIIcon
                    v-else
                    name="arrow-right"
                    size="10"
                    class="user-menu-panel__cta-chevron"
                />
            </button>

            <ul class="user-menu-panel__list">
                <slot />
            </ul>
        </div>

        <template #footer>
            <div v-if="$slots.footerLeading" class="user-menu-panel__footer-leading">
                <slot name="footerLeading" />
            </div>

            <button
                type="button"
                class="user-menu-panel__logout"
                @click="$emit('logout', $event)"
            >
                <UIIcon name="logout" size="14" />
                {{ t('core.components.UIUserMenuPanel.logoutLabel') }}
            </button>
        </template>
    </component>
</template>

<style lang="scss" scoped>
.user-menu-panel {
    &__header {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        border-bottom: 1px solid var(--border-surface, $panel-border-color);
    }

    &__header--dropdown {
        grid-template-columns: 1fr;
        border-bottom: none;
        padding-bottom: 8px;
    }

    &__identity {
        min-width: 0;
    }

    &__username {
        margin: 0;
        font-size: $font-size-lg;
        font-weight: $text-bold;
        color: var(--on-surface, #111);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    // Holds the username's line height, so the name doesn't push the panel down when it lands.
    &__username-skeleton {
        position: relative;
        display: block;
        width: 50%;
        height: $font-size-lg;
        margin: 4px 0;
        overflow: hidden;
        border-radius: var(--border-radius-sm, $border-radius-sm);
        background-color: $skeleton-color;

        @include skeleton-shimmer;
    }

    &__display-name {
        margin: 0;
        font-size: $font-size-sm;
        color: var(--on-surface, #111);
        opacity: 0.6;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        color: var(--on-surface, #111);
        border-radius: 50%;
        cursor: pointer;

        &:hover {
            background-color: rgba(0, 0, 0, 0.05);
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px 20px;
    }

    &__cta {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 12px 16px;
        border-radius: var(--border-radius-rounded, 999px);
        border: none;
        text-align: left;
        cursor: pointer;
        transition:
            filter 0.15s ease-out,
            transform 0.1s ease-out;

        &:hover {
            filter: brightness(1.05);
        }

        &:active {
            transform: scale(0.99);
        }

        &--primary {
            padding: 12px 16px 12px 12px;
            background-color: var(--color, var(--primary));
            color: var(--on-color, var(--on-primary));
            min-height: 56px;
        }

        &--secondary {
            background-color: var(--surface-soft, $panel-border-color);
            color: var(--on-surface, #111);
            min-height: 48px;
        }
    }

    &__cta-icon {
        flex-shrink: 0;
    }

    &__cta--primary &__cta-icon {
        background-color: rgba(255, 255, 255, 0.15);
        color: var(--on-primary);
        width: 20px;
        height: 20px;
        padding: 6px;
        border-radius: 50%;
        box-sizing: content-box;
    }

    &__cta-text {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
    }

    &__cta-title {
        font-size: $font-size-base;
        font-weight: $text-bold;
    }

    &__cta-subtitle {
        font-size: $font-size-xs;
        font-weight: $text-medium;
        opacity: 0.85;
        margin-top: 3px;
    }

    &__cta-chevron {
        opacity: 0.9;
    }

    &__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        height: 24px;
        padding: 0 8px;
        border-radius: var(--border-radius-rounded, 999px);
        background-color: var(--color, var(--primary));
        color: var(--on-color, var(--on-primary));
        font-size: $font-size-xs;
        font-weight: $text-bold;
    }

    &__list {
        list-style: none;
        margin: 8px 8px 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;

        // Force dark text/icons regardless of UIListLink's themed `--color`.
        :slotted(.list-link__wrap) {
            --color: var(--on-surface, #111);
            --on-color: var(--on-surface, #111);
        }

        :deep(.list-link) {
            padding: 4px 4px;
            font-size: $font-size-base;
            font-weight: $text-semi-bold;
            gap: 18px;
        }

        // Shrink the trailing chevron — UIListLink hard-codes size="18",
        // and :slotted() doesn't accept combinators, so we use :deep().
        :deep(.list-link > .icon:last-child) {
            width: 8px !important;
            opacity: 0.4;
        }
    }

    &__footer-leading {
        padding: 10px 18px 16px;
    }

    &__logout {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        width: 100%;
        padding: 14px 16px;
        border: none;
        background: transparent;
        color: var(--on-surface, #111);
        font-size: $font-size-sm;
        font-weight: $text-medium;
        cursor: pointer;

        &:hover {
            background-color: var(--surface-soft, rgba(0, 0, 0, 0.04));
        }
    }
}
</style>
